// Single source of truth for in-app release notes.
// Add newest release at the top of this array.
export const releaseNotes = [
    {
        id: '2026-10-23-translation-levels',
        title: 'Platform Updates - October 23, 2026',
        publishedOn: 'October 23, 2026',
        summary: 'Translation L1 and L2 tasks can now move the editor too.',
        features: [
            {
                title: 'Translation L1 and L2 linked to the editor',
                description:
                    'Translation Round 1 and Round 2 are now called Translation L1 and Translation L2. Once turned on, starting or completing L1 or L2 in a task also starts or submits that level in the editor, and sending a task back moves the editor back.',
            },
            {
                title: 'Security fix',
                description: 'Internal server diagnostics are no longer reachable from the web.',
            },
        ],
    },
    {
        id: '2026-09-11-bulk-export-speedup',
        title: 'Platform Updates - September 11, 2026',
        publishedOn: 'September 11, 2026',
        summary: 'Bulk reel export is now faster when a batch includes the same video in multiple languages or resolutions.',
        features: [
            {
                title: 'Faster bulk export for shared videos',
                description:
                    'When a bulk export batch includes the same source video multiple times (e.g. several languages or resolutions of one video), the source video is now downloaded once and reused across those jobs instead of being re-downloaded for each one, reducing overall export time.',
            },
        ],
    },
    {
        id: '2026-08-25-reel-editor-fixes',
        title: 'Platform Updates - August 25, 2026',
        publishedOn: 'August 25, 2026',
        summary: 'Reel Editor fixes: scoped style results now show in a popup, duplicate projects are blocked, resolutions are simpler, and a few UI bugs are fixed.',
        features: [
            {
                title: 'Apply Style results popup',
                description:
                    'Applying a style to a scope now shows a popup listing exactly which language/resolution projects were updated (and any that failed), instead of a plain banner message.',
            },
            {
                title: 'Fix: "Apply to all" in one language returning No projects',
                description:
                    'Fixed a bug where choosing "All resolutions of this language" could incorrectly report no matching projects. The project list is now refreshed right before applying, and language/resolution matching is no longer case- or whitespace-sensitive.',
            },
            {
                title: 'Fix: success banner staying visible after saving a new project',
                description:
                    'The "Ready for a new project" banner now clears automatically once the project is saved successfully.',
            },
            {
                title: 'Duplicate project prevention',
                description:
                    'You can no longer create two projects for the same video with the same language and resolution combination. Save now warns you and points you to the existing project instead.',
            },
            {
                title: 'Simplified resolution choices',
                description:
                    'The Format picker has been replaced with a Resolution picker offering three straightforward options: Horizontal (16:9), Vertical (9:16), and Square (1:1), instead of a list of platform names that mostly overlapped.',
            },
            {
                title: 'Fix: delete confirmation popup appearing behind the project dropdown',
                description:
                    'The delete confirmation popup could render behind the open project selector. Popups now always appear on top.',
            },
            {
                title: 'Fix: "Apply Style To This Project" staying disabled',
                description:
                    'This button previously required the video playhead to be sitting exactly on the selected subtitle, so it could stay disabled even with a subtitle selected. It now only requires a subtitle to be selected.',
            },
        ],
    },
    {
        id: '2026-08-17-reel-editor-scoped-apply-style',
        title: 'Platform Updates - August 17, 2026',
        publishedOn: 'August 17, 2026',
        summary: 'Reel Editor: "Apply to all" is now a scope picker, so you can propagate style changes to just the resolutions or languages you choose instead of everything at once.',
        features: [
            {
                title: 'Scoped "Apply Style To" in Reel Editor',
                description:
                    'The old "Apply to all projects" checkbox has been replaced with an "Apply style to" choice: This project only, All resolutions of this language, All languages of this resolution, or Everything. Set up your caption styling once, then push it to exactly the variants you mean to update — no more accidentally overwriting a language or resolution you didn\'t intend to touch.',
            },
        ],
    },
    {
        id: '2026-08-15-editing-trends-tab',
        title: 'Platform Updates - August 15, 2026',
        publishedOn: 'August 15, 2026',
        summary: 'Added a new Trends tab to Editing Stats showing edit rate trends over time, with shared deployment markers you can plot alongside the data.',
        features: [
            {
                title: 'Editing Trends Tab',
                description:
                    'A new "Trends" tab on the Editing Stats page charts text and timing edit rates over time, with adjustable smoothing and an option to show raw data points alongside the smoothed trend.',
            },
            {
                title: 'Deployment Markers',
                description:
                    'You can now add labeled markers (with an optional description) to the Trends chart to note deployments or other events. Markers are saved on the server and shared across all users and browsers, and can be selected from a dropdown to edit or remove.',
            },
        ],
    },
    {
        id: '2026-07-28-multi-column-viewer-fix',
        title: 'Platform Updates - July 28, 2026',
        publishedOn: 'July 28, 2026',
        summary: 'Fixed an issue where opening a video in the Multi-Column Subtitle Viewer from the file menu could fail to load.',
        features: [
            {
                title: 'Fix: Multi-Column Subtitle Viewer failing to load video',
                description:
                    'Opening a video in the Multi-Column Subtitle Viewer from the right-click menu could sometimes fail to load the video and its language subtitles correctly. This has been fixed, so the viewer now reliably loads the video details and available languages every time.',
            },
        ],
    },
    {
        id: '2026-07-18-donor-impact-timeline',
        title: 'Platform Updates - July 18, 2026',
        publishedOn: 'July 18, 2026',
        summary:
            'Added a year-over-year impact timeline to the Donor Impact Report, including a new Gujarati words translated metric.',
        features: [
            {
                title: 'Impact Timeline: This Year, Last Year, Since Start',
                description:
                    'The Donor Impact Report now shows a side-by-side timeline comparing this year, last year, and totals since the platform started, so donors can see growth over time at a glance.',
            },
            {
                title: 'Gujarati Words Translated metric',
                description:
                    'The timeline now tracks the total number of Gujarati words translated across subtitles and text files for each period, giving a concrete sense of translation volume over time.',
            },
        ],
    },
    {
        id: '2026-07-18-platform-updates',
        title: 'Platform Updates - July 18, 2026',
        publishedOn: 'July 18, 2026',
        summary:
            'Added feature usage tracking and metrics dashboards, made session timeout configurable, added text animation support in the Reel Editor, and fixed an MP3 upload issue.',
        features: [
            {
                title: 'Feature Usage Tracking',
                description:
                    'The platform now records feature usage across editors so admins can see which tools and workflows are actively being used.',
            },
            {
                title: 'Metrics Dashboard',
                description:
                    'New metrics have been added to give admins better visibility into platform activity and usage trends.',
            },
            {
                title: 'Configurable Session Timeout',
                description:
                    'Session timeout duration is now configurable, allowing admins to control how long a session stays active before requiring re-login.',
            },
            {
                title: 'Text Animation Support',
                description:
                    'The Reel Editor now supports text animations, letting subtitles and overlays animate in and out for more engaging video output.',
            },
            {
                title: 'Fix: MP3 upload issue',
                description:
                    'Resolved an issue that was preventing MP3 audio files from uploading correctly.',
            },
        ],
    },
    {
        id: '2026-06-28-new-fonts',
        title: 'Platform Updates - June 28, 2026',
        publishedOn: 'June 28, 2026',
        summary:
            'Added 5 distinctive fonts for video and reel creation — Anton, Bangers, Pacifico, Caveat, and Abril Fatface — along with a fix for fonts not matching in exported videos.',
        features: [
            {
                title: 'New fonts: Anton, Bangers, Pacifico, Caveat, Abril Fatface',
                description:
                    'Five expressive fonts popular in video and reel content are now available in the Reel Editor. Anton is a bold impact font great for titles, Bangers has a comic-book energy, Pacifico is a retro brush script, Caveat looks handwritten, and Abril Fatface delivers a high-contrast editorial style.',
            },
            {
                title: 'Fix: Font selected in Reel Editor now appears correctly in exported video',
                description:
                    'Resolved an issue where the font chosen in the editor was ignored during video export and a default system font was used instead. Exported videos now render exactly the font the user selected.',
            },
        ],
    },
    {
        id: '2026-06-28-platform-updates',
        title: 'Platform Updates - June 28, 2026',
        publishedOn: 'June 28, 2026',
        summary:
            'Fixed an issue where highlights and comments in the Text Editor were not persisted across sessions.',
        features: [
            {
                title: 'Fix: Text Editor comments now persist across sessions',
                description:
                    'Resolved a bug where highlights and comments added in the Text Editor were lost on reload. Comments are now correctly saved to the server on every save and restored when the document is reopened.',
            },
        ],
    },
    {
        id: '2026-06-18-platform-updates',
        title: 'Platform Updates - June 18, 2026',
        publishedOn: 'June 18, 2026',
        summary: 
            'Added a search text button, advanced button for searching within SRT files. In the Text File Details Dialog, now you can download the text files in PDF, Text, or DocX format',
        features: [
            {
                title: 'Search within SRT files',
                description: 'Search can be done now for all languages, filtering by language is enabled',
            },
            {
                title: 'Enable downloading of file from Text File Details Dialog',
                description: 'Now you can download the text files in PDF, Text, or DocX format',
            }

        ],
    },
    {
        id: '2026-06-10-platform-updates',
        title: 'Platform Updates - June 10, 2026',
        publishedOn: 'June 10, 2026',
        summary:
            'Refined the UI theme with improved color consistency and optimized shadow effects, and improved MP3 audio upload reliability.',
        features: [
            {
                title: 'Theme: Color and shadow refinement',
                description:
                    'Updated various UI components to ensure colors are perfectly matched and shadows provide consistent depth across editors, dialogs, and the dashboard.',
            },
            {
                title: 'Fix: MP3 upload reliability',
                description:
                    'Resolved an issue where MP3 audio uploads .',
            },
        ],
    },
    {
        id: '2026-06-09-platform-updates',
        title: 'Platform Updates - June 9, 2026',
        publishedOn: 'June 9, 2026',
        summary:
            'Added text highlighting and comments in the Text Editor, and fixed word emphasis stroke scaling in the Reel Editor.',
        features: [
            {
                title: 'Text Editor: Highlights and Comments',
                description:
                    'Users can now select text segments to highlight and attach comments. These annotations are saved as metadata and embedded within the document for easy review across sessions.',
            },
            {
                title: 'Fix: Reel Editor stroke width scaling',
                description:
                    'Fixed a bug where word emphasis stroke width appeared incorrectly. The stroke is now capped at 15% and correctly scales relative to the font size in both the preview and exported video.',
            },
        ],
    },
    {
        id: '2026-06-01-platform-updates',
        title: 'Platform Updates - June 1, 2026',
        publishedOn: 'June 1, 2026',
        summary:
            'Text Editor improvements and Downloading transformed dubbing audio now works reliably for large files.',
        features: [
            {
                title: 'Fix: Download transformed audio for large files',
                description:
                    'The Download Transformed button in the Audio File Details dialog failed silently for large audio files. The download now uses the browser\'s native streaming pipeline instead of buffering the full file in memory, and the download link is kept alive long enough for the transfer to start.',
            },
            {
                title: 'Text Editor Bug Fixes and UX Improvements',
                description:
                    'The text editor has been updated to improve the user experience: we have introduced independent visual zoom controls for both source and target panels, set the layout to default to vertical split view, turned off scroll synchronization by default, and made the text formatting toolbar fully collapsible.',
            },
        ],
    },
    {
        id: '2026-05-27-platform-updates',
        title: 'Platform Updates - May 27, 2026',
        publishedOn: 'May 27, 2026',
        summary: 'Reel editor is now stable and fully interactive — subtitles track playback, auto-scroll, and can be resized by dragging. Video editor auto-saves every minute and now shows when it was last saved. DubSync timelines are preserved across sessions. ',
        features: [
            
            {
                title: 'Fix: Shift+Enter phantom subtitle on timeline',
                description:
                    'Pressing Shift+Enter to split a subtitle in SubtitleEditor and SubtitleEditorV2 could leave a ghost block overlapping on the timeline that would shift position as you navigated. The split now correctly produces Sub instances with valid start/end times so the timeline renders both halves in their proper positions.',
            },
            {
                title: 'Fix: Undo broken after Shift+Enter split',
                description:
                    'Ctrl+Z (undo) had no effect after splitting a subtitle with Shift+Enter in SubtitleEditor. The split handler was capturing a stale copy of the state updater from the first render, so it saved an empty snapshot to the undo history instead of the real subtitle list. The handler now always references the latest updater via a ref.',
            },
            {
                title: 'Reel Editor: timeline, selection and playback improvements',
                description:
                    'Subtitle splitting (Shift+Enter) now works correctly with undo. The playhead cursor no longer resets on every frame. Clicking a subtitle in the timeline selects it and seeks the video; playback auto-selects the current subtitle. The timeline and subtitle list both auto-scroll to keep the active subtitle in view. Subtitle chips have draggable left/right handles to adjust start and end times directly on the timeline.',
            },
            {
                title: 'Video Editor: auto-save and DubSync track improvements',
                description:
                    'The editor auto-saves every 60 seconds and the Save button tooltip shows when it was last saved. DubSync VTT timelines are now persisted when the editor saves so cue positions are not lost between sessions. The DubSync bar is amber/gold to distinguish it from the base language subtitle bar. Any corrupted base language subtitle URLs are auto-healed on the next save.',
            },
        ],
    },
    {
        id: '2026-05-25-platform-updates',
        title: 'Platform Updates - May 25, 2026',
        publishedOn: 'May 25, 2026',
        summary:
            'The video name you enter is now preserved correctly throughout the transcription pipeline, stored video files are easier to identify by name, and the text file editor has a new up/down layout option with a tighter, cleaner appearance.',
        features: [
            {
                title: 'Video name preserved through transcription',
                description:
                    'When uploading a local video file, the title you provide is now sent to the transcription service as-is. Previously it was silently replaced by a cryptic server-generated filename, making it hard to trace which video was being processed.',
            },
            {
                title: 'Stored video files named with title first',
                description:
                    'Compressed video files saved to cloud storage are now named starting with the readable video title (e.g. my-video_a3f9b-…) instead of a timestamp prefix, making them much easier to identify when browsing storage.',
            },
            {
                title: 'Text editor up/down split layout',
                description:
                    'The text file editor now supports stacking the source and translation panels vertically (top/bottom) in addition to the existing side-by-side view. Toggle between layouts using the new button in the toolbar. In stacked mode, both panels are full-width, the editing toolbar sits directly above the translation panel, and whitespace is kept tight so more text is visible at once.',
            },
        ],
    },
    {
        id: '2026-05-23-platform-updates',
        title: 'Platform Updates - May 23, 2026',
        publishedOn: 'May 23, 2026',
        summary:
            'The version comparison view in File Details now highlights the exact words that changed, and scrolling through long comparisons is smooth and reliable.',
        features: [
            {
                title: 'Word-level highlights in version compare',
                description:
                    'When comparing two versions of a text file, changed words are now highlighted in red (removed) and green (added) within each line — making it much easier to spot small edits inside long paragraphs.',
            },
            {
                title: 'Fix: Comparison view now scrolls correctly',
                description:
                    'The File Details dialog now uses a single scroll container, so long diffs scroll smoothly without content being cut off or getting stuck.',
            },
        ],
    },
    {
        id: '2026-05-22-platform-updates',
        title: 'Platform Updates - May 22, 2026',
        publishedOn: 'May 22, 2026',
        summary:
            'You can now download text files in your choice of format. Blank English-VO cues no longer reappear after being deleted.',
        features: [
            {
                title: 'Download text files in HTML, TXT, DOCX, or PDF',
                description:
                    'The "Download by Language" option in the folder\'s Advanced menu now lets you choose the download format: HTML (preserves formatting), Plain Text (.txt), Word Document (.docx), or PDF (.pdf). The same conversion logic used in the Text Editor is reused, so formatting is consistent across both places.',
            },
            {
                title: 'Fix: "Download as Text" in the Text Editor no longer includes raw CSS',
                description:
                    'When using "Download as Text" from the Text Editor\'s Download menu, the .txt file previously included all the raw CSS styling rules at the top of the file — making the downloaded text unreadable for EPUB-sourced documents. The CSS is now stripped, so you get only the clean readable content.',
            },
            {
                title: 'Fix: Blank English-VO cues no longer reappear on reload',
                description:
                    'When English-VO has fewer cues than English, blank cues with English timestamps were being injected into the English-VO column every time the video was opened. Deleting them and saving had no lasting effect because the merge logic re-created them on each load. The merge now only produces cues that exist in English-VO, so extra English cues are ignored.',
            },
        ],
    },
    {
        id: '2026-05-18-platform-updates',
        title: 'Platform Updates - May 18, 2026',
        publishedOn: 'May 18, 2026',
        summary:
            'Dub Sync skips re-transcription when a prior run exists, DOCX export now preserves bold, italic, and paragraph alignment, and four subtitle editor bugs causing stale or incorrect data in dual-column (English-VO / translated language) view have been fixed.',
        features: [
            {
                title: 'Fix: Skip re-transcription in Dub Sync',
                description:
                    'Running Sync Magic a second time (or after a page reload) now checks the server first. If a DubSync transcription already exists, it skips the expensive API call and goes directly to alignment. The Add DubSync Track dialog also detects this state and lets you add the track without re-uploading audio.',
            },
            {
                title: 'Fix: DOCX export preserves formatting',
                description:
                    'Downloading a text file as DOCX now correctly retains bold, italic, underline, and paragraph alignment (center, right, justify) — including styles defined via CSS classes in EPUB/HTML source files.',
            },
            {
                title: 'Fix: Subtitle editor showed stale content for English-VO and dual-column languages',
                description:
                    'Four separate bugs were resolved that could cause the dual-column subtitle editor to display outdated text or wrong timings. (1) The editor always fetched the English VTT on load regardless of the active language — now fetches the correct language. (2) A race condition during language switch could briefly replace the target-language content with English content. (3) The browser could serve a cached VTT file even after a save — the editor now always fetches a fresh copy from the server. (4) English-VO and other translated-language timing edits were silently overwritten with English timings on every reload — the editor now preserves each language\'s own cue timings.',
            },
        ],
    },
    {
        id: '2026-05-17-platform-updates',
        title: 'Platform Updates - May 17, 2026',
        publishedOn: 'May 17, 2026',
        summary:
            'Fixed a stale-closure bug in the Subtitle Editor (V1 and V2) that could silently write subtitle data to the wrong language key in local storage, causing the wrong language to appear after a language switch.',
        features: [
            {
                title: 'Fix: Wrong language shown after language switch',
                description:
                    'Subtitle data is now always saved to the correct per-language storage key, even when an async merge callback fires after the user has already switched languages. The editor also now always refreshes from the server on load, healing any previously corrupted cache entries.',
            },
            {
                title: 'Fix: Reliable seek, usage stats, and richer Text File history',
                description:
                    'Jumping in videos now works reliably. We added helpful usage stats, and the Text Editor keeps a richer history so you can view and restore previous versions.',
            },
            {
                title: 'Improvement: File History',
                description:
                    'File History is clearer and more reliable — view past edits and restore older versions with ease.',
            },
            {
                title: 'Feature: Dub Sync in Video Editor',
                description:
                    'Dub Sync is now available in the Video Editor — align dubbed audio with subtitles and render synced audio right inside the editor.',
            },
        ],
    },
    {
        id: '2026-05-16-slovenian-reel-editor',
        title: 'Platform Updates - May 16, 2026',
        publishedOn: 'May 16, 2026',
        summary:
            'Added Slovenian language support and fixed Reel Editor pointer issues and Split Segment (audio) behavior.',
        features: [
            {
                title: 'Feature: Slovenian language added',
                description:
                    'Slovenian is now supported platform-wide for subtitles, translations, and UI language selection.',
            },
            {
                title: 'Fix: Reel Editor pointer and split audio segment',
                description:
                    'Fixed pointer/seek precision issues in the Reel Editor and resolved a bug where "Split Segment" failed silently for dubbed audio tracks; splits now cut at the playhead reliably.',
            },
        ],
    },
    {
        id: '2026-05-11-dubbing-upload-fix',
        title: 'Platform Updates - May 11, 2026',
        publishedOn: 'May 11, 2026',
        summary:
            'Fixed an issue where dubbing transformations for local audio uploads sometimes appeared to fail in the UI even though processing completed successfully on the server.',
        features: [
            {
                title: 'Fix: Subtitle newline preservation',
                description:
                    "I can press Enter to add line breaks in the subtitle textarea and they now remain after saving and re-opening the editor. Newlines are no longer stripped during the save/load cycle.",
            },
            {
                title: 'Fix: Dubbing audio upload reliability',
                description:
                    'Uploading a local audio file for dubbing now starts reliably and the processing tracker correctly shows progress and completion. The UI no longer shows a misleading "Failed" message for large or delayed backend operations.',
            },
            {
                title: 'Feature: DOCX download in Text Editor',
                description:
                    'Text Editor now supports exporting the current document as a DOCX file with preserved formatting and language labels via the Download menu.',
            },
            {
                title: 'Fix & Feature: Video Editor improvements',
                description:
                    'Multiple bug fixes and quality-of-life features in the Video Editor: timeline cursor and split-segment reliability fixes, improved multi-audio-track BGM handling, and more stable export behavior.',
            },
        ],
    },
    {
        id: '2026-05-03-platform-updates',
        title: 'Platform Updates - May 3, 2026',
        publishedOn: 'May 3, 2026',
        summary:
            'Split then Process is now fully asynchronous with live progress messages and clear inline errors. Several Reel Editor bugs are fixed: audio segment splitting now works correctly, clicking a subtitle chip no longer snaps the cursor to the wrong position, and the subtitle preview stays visible when the cursor is between subtitle blocks.',
        features: [
            {
                title: 'Fix: Split then Process reliability and progress',
                description:
                    'The backend now starts the job immediately (HTTP 202) and processes in the background. The dialog polls a new status endpoint every 5 seconds and shows the current stage — downloading, splitting, processing each part. If anything fails, a clear inline error message is shown. An underlying crash ("Stream closed") during FFmpeg silence detection is also fixed by ensuring output reader threads finish before the process is destroyed.',
            },
            {
                title: 'Fix: Reel Editor audio split and subtitle preview',
                description:
                    'Clicking an audio chip to select it no longer overwrites a manually positioned cursor, so "Split Segment" now works as expected. Clicking a subtitle chip positions the cursor at the exact click point instead of snapping to the subtitle start. The subtitle overlay in the video preview now stays visible when the cursor is between subtitle blocks by falling back to the last selected subtitle.',
            },
        ],
    },
    {
        id: '2026-04-28-platform-updates',
        title: 'Platform Updates - April 28, 2026',
        publishedOn: 'April 28, 2026',
        summary:
            'The Processing Status Dashboard gains interactive activity charts showing In Progress and Completed counts per day, week, or month — with duration and size charts for S2TT, T2TT, and Text Translation. Dubbing transformation history is now retained beyond the 24-hour audio cleanup window; expired records remain visible in the dashboard and are counted in the Donor Impact Report.',
        features: [
            {
                title: 'Activity charts by day, week, or month',
                description:
                    'Each tab (S2TT, T2TT, Text Translation, Dubbing) now shows a bar chart of processing activity over time, with In Progress and Completed counts grouped per time bucket. A toggle lets users switch between days, weeks, and months view; the default is automatically selected based on the active timeframe (e.g. days for last 24 h, weeks for last 30 days, months for yearly views).',
            },
            {
                title: 'Duration and size charts for S2TT, T2TT, and Text Translation',
                description:
                    'S2TT and T2TT tabs show a second side-by-side chart with the total video/audio duration processed per time bucket (formatted as hours and minutes). The Text Translation tab shows total file size (MB) processed per bucket instead. Dubbing shows only the item count chart.',
            },
            {
                title: 'Dubbing history preserved beyond 24-hour audio expiry',
                description:
                    'Previously, standalone dubbing audio records (AudioFileDocument) were hard-deleted from MongoDB when the Azure blob was removed after 24 hours, making it impossible to count or review past dubbing jobs. The cleanup job now performs a soft-delete: the blob and filesystem nodes are still removed, but the MongoDB document is retained with deleted=true. Per-video-language dubbing records were already preserved; they now additionally carry a dubbingAudioExpired flag so the frontend can distinguish completed-and-expired jobs from those still active.',
            },
            {
                title: 'Processing Status Dashboard: expired dubbing records shown',
                description:
                    'The Dubbing Transformations tab now includes records whose audio has expired. Expired rows are dimmed and show an "Audio Expired" chip instead of the status chip. The stats card gains an "Expired" counter alongside Total / In Progress / Completed.',
            },
            {
                title: 'Donor Impact Report: dubbing transformation counts',
                description:
                    'The Donor Impact Report now includes a "Dubbing Transformations" stat card showing the all-time total and the count completed within the selected report period (30 or 90 days). Both standalone audio and per-video-language dubbing completions are counted, including those whose audio has already been cleaned up.',
            },
            {
                title: 'Automatic 90-day purge of expired dubbing records',
                description:
                    'A daily scheduled job (runs at 02:30) permanently purges soft-deleted AudioFileDocument records that are older than 90 days, keeping the audiofiles collection from growing indefinitely while still retaining enough history for quarterly donor reports.',
            },
        ],
    },
    {
        id: '2026-04-27-platform-updates',
        title: 'Platform Updates - April 27, 2026',
        publishedOn: 'April 27, 2026',
        summary:
            'Split then Process now requires an English SRT for non-English/non-Gujarati languages. SRMD Studio gains intro/outro image playback and an Editor Controls panel. Reel Editor: fixed timeline cursor flash on subtitle click and split audio segment. Processing Status Dashboard adds a Dubbing Transformations tab.',
        features: [
            {
                title: 'Split then Process: mandatory English SRT for non-English/non-Gujarati languages',
                description:
                    'When using the Upload SRT subtitle mode and the selected language is not English or Gujarati, a second "English SRT (required)" upload box now appears. The user must upload the English version of the subtitles before submitting. Both SRT files are split by the same silence-adjusted timestamp boundaries and each video part receives correctly timed subtitles in both the primary language and English.',
            },
            {
                title: 'Video Editor: intro & outro image overlays with timeline playback',
                description:
                    'Image overlays can now be assigned an Intro, Watermark, or Outro role. Setting an intro duration shifts the video right on the timeline; setting an outro duration extends it at the end. Playback correctly shows intro images before the video starts and outro images after it ends — the preview no longer jumps straight to the video when the playhead is in an intro or outro region.',
            },
            {
                title: 'Video Editor: Editor Controls panel with language & track management',
                description:
                    'The left media panel is renamed "Editor Controls". It now houses subtitle preview language selection, per-language timeline track visibility toggles (show/hide individual language tracks to declutter the timeline), audio BGM tracks, and image overlay management — all in one place.',
            },
            {
                title: 'Reel Editor: timeline cursor flash and split audio segment fixes',
                description:
                    'Clicking a subtitle chip was causing the playhead to flash back to position 0; the cursor-sync effect now re-fires on selection changes to keep it stable. Clicking an audio segment chip now also updates the current-time position, so the Split Segment button works correctly instead of failing silently.',
            },
            {
                title: 'Processing Status Dashboard: Dubbing Transformations tab',
                description:
                    'The Processing Status Dashboard now includes a "Dubbing Transformations" tab alongside the existing Speech-to-Text, Text-to-Text, and Text Translation tabs. It shows total, completed, and in-progress dubbing jobs for the selected time window, average processing time, and a per-job table with name, created by, type (Video/Audio), source language, started time, status, and processing time. Statistics are stored in MongoDB and persist beyond the 24-hour audio file cleanup window.',
            },
        ],
    },
    {
        id: '2026-04-24-platform-updates',
        title: 'Platform Updates - April 24, 2026',
        publishedOn: 'April 24, 2026',
        summary:
            'Contextual help tours added to all editors and dialogs. Text File edit session locking extended from subtitles to the Text Editor. Dubbing audio transformation fixed end-to-end for local file uploads. Currently Online tab added to Active Sessions page. Video Editor (SRMD Studio) gains multi-audio-track BGM support, per-track mute, and video audio mute.',
        features: [
            {
                title: 'Contextual help tours',
                description:
                    'All major editors (Subtitle, Video, Reel, Text) and dialogs (New Video, Split Video, New Text File, Manage Progress Stages, Dubbing Audio) now have a built-in help tour. Clicking ? opens a step-by-step walkthrough that highlights each section of the UI with a description of what it does. In dialogs the ? button appears in the title bar; in editors it appears as a fixed button at the bottom-right of the screen.',
            },
            {
                title: 'Text File edit session management',
                description:
                    'Opening a text file in the Text Editor now starts a locking session for the active right-panel language. If a second user (or a second browser tab) opens the same language, they receive a read-only warning banner that stays pinned below the toolbar and cannot be scrolled away. The editor, formatting toolbar, and auto-save are all disabled for the locked viewer. Sessions are kept alive with a 5-minute heartbeat and are automatically released when the user navigates away, closes the tab, or switches to a different language tab. Stale sessions (no activity for 15 minutes) are cleaned up by the scheduler. The Active Sessions admin page now shows text file sessions with an orange "Text File" chip alongside the existing subtitle and section session types, and admins can end any text file session from that page.',
            },
            {
                title: 'Dubbing audio transformation — local file upload fixed',
                description:
                    'Fixed several bugs that prevented local MP3 / audio file uploads from working in the Transform Dubbing Audio dialog. The file picker now correctly clears between selections so the same file can be re-picked. The backend no longer crashes with FileNotFoundException when saving the uploaded file (Tomcat\'s working directory was being used instead of the system temp directory). A malformed blob filename (extension embedded in the middle of the name) that caused Azure upload failures is also resolved. Finally, Azure Blob URLs are now sent to the Samvaad model via the correct input_file field rather than file_url — fixing the "Predictor object has no attribute input_mode" error that occurred when the model could not detect the URL type.',
            },
            {
                title: 'Currently Online users in Active Sessions page',
                description:
                    'Admins and team leads can now see who is currently logged in directly from the Active Editing Sessions page. A new "Currently Online" tab (alongside "My Sessions" and "All Users\' Sessions") lists every user who has sent a heartbeat in the last 30 minutes, showing their name, email, role, and how long ago they were last active.',
            },
            {
                title: 'Video Editor: multi-audio-track BGM, per-track mute, and video mute',
                description:
                    'SRMD Studio now supports multiple background audio tracks simultaneously. The Media Library panel lets you add as many BGM files as needed — each appears as its own draggable bar on the timeline with independent volume and offset controls. Right-clicking any track label in the timeline opens a context menu to mute/unmute that individual audio track or (for the video track) mute/unmute the video\'s own audio. The mute state is preserved in the export payload so the backend renders with the correct audio mix. The play button state is also fixed to stay in sync with actual video playback.',
            },
        ],
    },
    {
        id: '2026-04-23-platform-updates',
        title: 'Platform Updates - April 23, 2026',
        publishedOn: 'April 23, 2026',
        summary:
            'Video Editor (SRMD Studio, experimental) added to the Video / Audio Processing file menu section — full NLE-style editor with cut/subtitle cascade, Media Library panel for BGM audio and logo watermark overlays, and export wired to the backend API. Reel Editor receives multiple UX and bug fixes. DubSync first pass, Text Editor synchronized scrolling, and Donor Impact Report text stats also shipped.',
        features: [
            {
                title: 'Split then Process: SRT upload fixes and validation',
                description:
                    'Fixed several bugs in the Split then Process (Upload SRT) flow: the SRT content is now correctly saved and not overwritten by empty placeholder files; OptimisticLockingFailureException errors are prevented by re-fetching the latest document version before each save; when no additional target languages are selected the video is created with only the SRT language slot; English is always ordered first in the language list when created alongside a non-English SRT; and a validation error is shown if the user tries to submit without uploading an SRT file.',
            },
            {
                title: '⚗️ Video Editor (SRMD Studio) — experimental, under active development',
                description:
                    'A new "Video / Audio Processing" section in the three-dots file menu groups Reel Editor, Video Editor (Studio), and DubSync together. Opening Video Editor launches SRMD Studio — a full-screen NLE-style editor with a program monitor (video preview with subtitle overlay and cut-skip playback), a draggable resizable split, and a multi-track timeline showing the video segment and one subtitle track per language. Cut markers can be placed on the timeline; confirming a ripple delete removes the segment and automatically cascades the trim across all language subtitle tracks simultaneously. The top bar combines breadcrumb navigation, title, and Export button in a single row, adapting to light and dark mode. A collapsible Media Library side panel (Media button in the top bar) lets you drag-and-drop or browse for a BGM audio file (MP3/WAV/AAC/OGG) and a logo image (PNG/JPG/SVG/WebP); both appear as draggable/resizable blocks on the timeline with volume, opacity, position, and timing controls. The Export dialog sends all cut, music, and logo fields to the backend via /api/v3/videos/{id}/export using the app\'s standard auth headers.',
            },
            {
                title: 'Reel Editor: bug fixes and UX improvements',
                description:
                    'Timeline cursor no longer resets to zero when dubbed audio is uploaded or when clicking/dragging; subtitle chip clicks seek the video to the subtitle start time; cursor drag/ruler click auto-selects the matching subtitle block. Shape and Image overlays gained Fade In / Fade Out (ms) fields (local scope defaults to subtitle start/end ms). Scope selector replaced with radio buttons defaulting to Selected Subtitle; Enable toggle moved above scope on both panels. Shape Radius (px) control added. Delete/Backspace key removes the selected subtitle block when focus is not in a text field. Uploading a global logo pre-fills Show from / Hide after from the selected subtitle. All four right-column panels are now collapsible.',
            },
            {
                title: 'DubSync (first pass — needs testing and UX review)',
                description:
                    'New DubSync page (/dubsync) provides a 4-step pipeline for aligning dubbed audio to updated subtitle timings. Step 1: Transcribe — creates a language-specific DubSync track by reading the original subtitles and generating a timing-shifted transcription for comparison. Step 2: Review & Edit Text — side-by-side diff of original vs transcribed cue text; user confirms or edits before proceeding. Step 3: Align & Edit Timeline — aligns timestamps then opens a drag-and-drop timeline editor so the user can fine-tune each cue\'s start/end position and save back to Azure. Step 4: Render Audio — runs a real ffmpeg pipeline that extracts each cue segment from the dubbed MP3, applies atempo stretching/compression to match the new target duration, inserts silence for gaps, concatenates all adjusted segments, and uploads the rendered audio to Azure Blob. The rendered MP3 is playable in-browser and available for download.',
            },
            {
                title: 'Text Editor: synchronized scroll between source and translation',
                description:
                    'When viewing a text file side-by-side, both panels now scroll in sync by default. Scrolling either the source or the translated panel proportionally moves the other so the same part of the document stays visible in both panes. A toggle button (⇄) in the top toolbar lets users enable or disable scroll sync at any time; the button is highlighted when sync is active.',
            },
            {
                title: 'Donor Impact Report: text translation stats',
                description:
                    'The Donor Impact Report now includes two additional stat cards: "Text Files" (total text documents on the platform with count added in the period) and "Text Translations" (total completed text-to-text translations with count completed in the period). These are sourced from the textfiles collection and use the same translationCompletedAt signal as video subtitle counts. SOURCE_LANGUAGE entries are excluded so only actual translated outputs are counted.',
            },
        ],
    },
    {
        id: '2026-04-22-platform-updates',
        title: 'Platform Updates - April 22, 2026',
        publishedOn: 'April 22, 2026',
        summary:
            'Group Management admin page now shows member counts per group and a new "Users View" tab for seeing which groups each user belongs to. Reel Editor receives a major UX overhaul: subtitle style section moved above word emphasis, numeric input "012" bug fixed, color pickers replaced with a proper ChromePicker popover, shadow color default changed to white, word stroke capped at 15, playhead now tracks playback, top-position subtitles no longer go off-screen, image overlay gains timing controls, audio segments can be deleted, and the timeline is extracted into a reusable SubtitleTimeline component. Donor Impact Report is enhanced with accurate subtitle counts, live CPU usage, donated server specs, and a cloud cost comparison table. PDF filename is now descriptive and date-stamped.',
        features: [
            {
                title: 'Group Management: member count and Users View',
                description:
                    'The Groups table now displays a "# Members" count chip for each group so admins can quickly see group sizes at a glance. A new "Users View" tab has been added to the Group Management page that lists every user with their email address, a count of how many groups they are in, and the name of each group shown as labeled chips.',
            },
            {
                title: 'Donor Impact Report: accuracy and server details',
                description:
                    'Fixed "Total Subtitle Tracks" always showing 0 — the filter now correctly detects completed subtitle tracks via translationCompletedAt instead of the internal processing status field. The CPU usage gauge no longer spins indefinitely; it now shows a live system load average (normalized by processor count) that works on both Mac and Ubuntu. A new "Donated Server Hardware" section shows spec cards for both servers (4 vCPU / 15 GB RAM / 400 GB SSD, Ubuntu 20.04, VMware, uptime). A new "Equivalent Cloud Cost" section compares what the two servers would cost to rent on AWS, Google Cloud, DigitalOcean, and Hetzner, with a summary showing the annual donation value of $3,720–$4,680. The Print / Save as PDF button now sets the filename automatically to AmrutVaani-DonorImpact-30Days-mm-dd-yyyy.',
            },
            {
                title: 'Reel Editor: UX fixes and improvements',
                description:
                    'Multiple fixes and improvements across the Reel Editor: the "Entire Subtitle Style" section now appears above "Word Emphasis" in the editing panel; all numeric fields are replaced with a custom NumericField component that eliminates the "012" browser input bug and auto-selects on focus; all color fields are replaced with a ChromePicker popover (ColorField) that updates the hex value in real time while dragging sliders and always works regardless of the starting color; the word emphasis shadow color default is now white (was black and invisible on dark backgrounds); word stroke is capped at 15%; the timeline playhead now tracks video playback; top-positioned subtitles are anchored within the safe area instead of going off-screen; changing the Position dropdown resets Offset X/Y to zero; the global Image Overlay section gains "Show from (s)" and "Hide after (s)" timing fields; a "Delete Segment" button is added to the Audio Track panel; and the timeline is extracted into a reusable SubtitleTimeline component.',
            },
        ],
    },
    {
        id: '2026-04-18-donor-impact-report',
        title: 'Platform Updates - April 18, 2026',
        publishedOn: 'April 18, 2026',
        summary:
            'New Donor Impact Report page lets admins generate a 30- or 90-day summary of platform usage and server health to share with server donors. V2 Subtitle Editor speed controls cleaned up. Active Editing Sessions and Editing Statistics now track editor version (V1/V2).',
        features: [
            {
                title: 'Donor Impact Report (Admin)',
                description:
                    'Admins can now generate a self-serve impact report from the Admin Dashboard → "Generate Report". Select a 30-day or 90-day period and click Generate to get a live summary including: total videos on the platform and how many were added in the period, total completed subtitle tracks and how many were generated in the period, language breakdown table, active volunteer count, server health gauges (memory, disk, uptime). The report can be printed or saved as a PDF directly from the browser to share with the server donors.',
            },
            {
                title: 'V2 Editor: speed and playback controls consolidated',
                description:
                    'In the V2 Subtitle Editor, the old speed buttons (0.5x / 1x / 1.25x / 1.5x / 2x) and the rewind / fast-forward buttons that appeared inside the video player area have been removed. The speed chip row in the control bar below the video is now the sole place to change playback speed. The V1 editor is unaffected.',
            },
            {
                title: 'Editor version tracking in Active Sessions and Editing Statistics',
                description:
                    'Every new editing session now records whether it was opened with the V1 or V2 subtitle editor. In the Active Editing Sessions page a new "Editor" column shows a green V2 chip or an outlined V1 chip for each live session. In the Editing Statistics leaderboard, a new "Editor" column shows a per-user breakdown of how many sessions were in V1 vs V2 (e.g. "V2: 12", "V1: 5"). Historical sessions with no version recorded are counted and displayed as V1.',
            },
        ],
    },
    {
        id: '2026-04-16-audio-file-download',
        title: 'Platform Updates - April 16, 2026',
        publishedOn: 'April 16, 2026',
        summary:
            'Audio file downloads now use the audio file name. Glossary Editor now queues multiple proposals per term. Reel Editor gains a per-language dubbed audio track panel with mute toggle, file upload, timeline segment editing, and split-segment support.',
        features: [
            {
                title: 'Audio File Details: named downloads',
                description:
                    'The "Download Original" and "Download Transformed" buttons in the Audio File Details dialog now save files using the audio file\'s name as the filename (e.g. MyAudio_original.wav and MyAudio_transformed.wav). Previously the downloaded file used the internal ID. Downloads are streamed through the backend to avoid browser CORS restrictions on Azure Blob Storage URLs.',
            },
            {
                title: 'Glossary Editor: multi-proposal review (queue all submissions per term)',
                description:
                    'When multiple users submit different translations for the same source word, all proposals are now queued and shown in the Pending Review panel — one row per proposal. The reviewer sees each option with the proposed value and the name of who submitted it, and can approve any one proposal (applying it and discarding the rest) or reject all proposals for that word. "Approve All" picks the most recent proposal per word. Previously, a later submission silently overwrote an earlier one.',
            },
            {
                title: 'Reel Editor: per-language dubbed audio track',
                description:
                    'The Reel Editor now includes an Audio Track panel placed directly below the timeline. The panel is scoped to the active language — switching languages saves the current audio state and restores the previously uploaded audio for the new language, so each language can carry its own dubbed soundtrack. Controls include a Mute Video Audio toggle (immediately mutes/unmutes the preview), an Upload Dubbed Audio button that loads any audio file and creates a full-length segment on a teal-coloured audio row in the timeline, a filename chip showing segment count with a clear button, and a Split Segment button that divides the selected segment at the playhead position. Audio segments can be freely dragged and resized on the timeline. During playback, the dubbed audio element is kept in sync with the video (auto-seeking on play, pause, and scrub events). On export, the manifest includes muteVideoAudio and the audioTrack (src + segments) so the backend can composite the dubbed audio into the rendered video.',
            },
        ],
    },
    {
        id: '2026-04-15-text-editor-language-access',
        title: 'Platform Updates - April 15, 2026',
        publishedOn: 'April 15, 2026',
        summary:
            'Text Editor now respects per-language user access. Glossary Editor adds a full submit-for-review and approval workflow. Reel Editor redesigned to a two-column layout so the video stays visible while editing.',
        features: [
            {
                title: 'Text Editor: per-language access control',
                description:
                    'The Text Editor now enforces the same language-access permissions used in the video subtitle editor. When a user switches to a translation tab, the editor checks their languageAccess for that language. Users with WRITE or ADMIN access see the editor in full edit mode (green EDIT badge). Users with READ-only access see the editor, formatting toolbar, and Save button all disabled, with an orange READ badge displayed in the toolbar. Auto-save is also suppressed for read-only languages.',
            },
            {
                title: 'Glossary Editor: submit-for-review and approval workflow',
                description:
                    'All glossary changes (add, edit, delete) now go through a staging queue before being applied. Any user — including admins — submits changes for review using the "Submit for Review" button. Glossary Managers and Admins see a Pending Review panel that lists all staged changes with full details (current value, proposed value, submitted by). Each term can be individually approved or rejected, or all can be approved/rejected at once. Approved changes are applied to the live glossary via the AI server; rejected changes are discarded. The Subfolder field now shows a dropdown of existing subfolders sourced from the AI server (root, test, vachanamrutiji, etc.) while still allowing free-text entry for new subfolders.',
            },
            {
                title: 'Reel Editor: two-column layout',
                description:
                    'The Reel Editor now uses a two-column layout. The narrower left column shows the video preview at the top and the subtitle blocks list below it; the wider right column shows the timeline and all editing controls. The left column is sticky so the video stays in view while scrolling through the editing controls on the right. The timeline height has also been increased for easier cue selection.',
            },
        ],
    },
    {
        id: '2026-04-14-platform-updates',
        title: 'Platform Updates - April 14, 2026',
        publishedOn: 'April 14, 2026',
        summary:
            'Added a Change Level dialog in the Text Editor to update process stages, supplemental role assignment (Dubber and Glossary Manager) for users in User Management, Reel Editor enhancements (split subtitle, push to subtitle source, auto-save reliability fix), and a language selector when uploading an SRT for split video.',
        features: [
            {
                title: 'Text Editor: Change Level dialog',
                description:
                    'A "Change Level" button is now available in the Text Editor toolbar. Clicking it opens a dialog that shows the current process stage for the active translation language and lets the user advance it to any selectable stage: Level 1 Complete, Level 2 WIP, Level 2 Complete, Level 3 WIP, Level 3 Complete, or Published. WIP stages are shown for reference but cannot be selected as a save target. The change is saved immediately to the backend and the file is reloaded to reflect the new stage.',
            },
            {
                title: 'User Management: supplemental role assignment',
                description:
                    'Admins can now assign additional roles — Dubber and Glossary Manager — to users whose primary role is "User" or "Admin". These supplemental roles appear as checkboxes in the edit-user dialog. Users with the Dubber or Glossary Manager role gain access to the same features as primary-role holders. Existing users whose primary role was "Dubber" are transparently migrated: their role becomes "User" with "Dubber" listed as a supplemental role, so access is preserved without any manual action.',
            },
            {
                title: 'Reel Editor enhancements and fixes',
                description:
                    'Three updates to the Reel Editor: (1) A "Split" button beside the Selected Subtitle Text panel divides the selected cue into two at the word midpoint, each receiving the appropriate half of the original time range (disabled when only one word is present). (2) A new "Push to Subtitle Source" action writes the current reel text and timing back to the underlying VTT file — after a confirmation prompt noting that style changes are excluded, the cues are serialised as WEBVTT and uploaded to the source blob URL, with a toast confirming how many subtitles were saved. (3) The auto-save timer now always calls the latest version of the save function via a ref, preventing a stale-closure bug that could silently discard recent changes.',
            },
            {
                title: 'Split Video: language selector for uploaded SRT',
                description:
                    'When creating a new "Split then Process" video with an uploaded SRT file, users can now choose the language of that SRT from a dropdown. The selected language is forwarded to the backend so each split part receives subtitles in the correct language.',
            },
        ],
    },
    {
        id: '2026-04-10-v2-subtitle-editor',
        title: 'Platform Updates - April 10, 2026',
        publishedOn: 'April 10, 2026',
        summary:
            'Introduced the V2 Subtitle Editor with a three-panel resizable layout, virtualized subtitle list, timeline, and full feature parity with V1 including session management, edit tracking, conflict detection, and an overlap count badge. Added "New Video (Split then Process)" to split a video into equal parts and process each part independently.',
        features: [
            {
                title: 'New Video (Split then Process)',
                description:
                    'A new "New Video (Split then Process)" option is now available under the New menu. Upload a video from a local file or Google Drive, choose the number of equal parts to split it into, and each part is automatically processed through the full pipeline. Supports three subtitle modes: AI-generated subtitles (speech-to-text runs on each part), Skip (empty subtitle files are created), or Upload SRT (a single SRT file is uploaded and automatically split by timestamp so each part receives its own correctly-timed subtitles). Each part appears in the bottom-left progress panel and is tracked individually. Parts are named "{Video Name} - Part 1", "Part 2", etc.',
            },
            {
                title: 'New V2 Subtitle Editor',
                description:
                    'The subtitle editor has been rebuilt with a three-panel resizable layout (video, subtitle list, timeline) all visible at once. The subtitle list is fully virtualized for smooth performance on long files. Arrow-key navigation, follow-playhead auto-scroll, find-and-replace, undo, and all editing operations work as they did in V1. An overlap count badge in the toolbar shows the number of timing conflicts in real time and clears automatically as they are fixed.',
            },
            {
                title: 'Full session and edit tracking parity with V1',
                description:
                    'The V2 editor captures all the same events as V1: session open and close, language changes, keep-alive heartbeats, inactivity timeout, manual save, auto-save, import, export, and per-subtitle edits (text, timing, add, delete, merge). Conflict detection behaves identically to V1, including the detailed "Already Open" popup with steps to resolve stale sessions.',
            },
        ],
    },
    {
        id: '2026-04-07-platform-updates',
        title: 'Platform Updates - April 7, 2026',
        publishedOn: 'April 7, 2026',
        summary:
            'Reel Editor enhancements including auto-save, auto-load, overlap detection in the Subtitle Editor, and a fix for reel video export timing out.',
        features: [
            {
                title: 'Reel Editor: auto-save on changes',
                description:
                    'The Reel Editor now automatically saves the current project 4 seconds after the last change is made (subtitle edits, style changes, overlay adjustments, fade timings). Auto-saves run silently in the background with no toast interruption. The manual Save button continues to work as before and still shows the confirmation toast.',
            },
            {
                title: 'Reel Editor: auto-load first project on open',
                description:
                    'When opening the Reel Editor, the first project in the project list is now automatically loaded, so users land directly in their most recent work without having to manually select a project from the dropdown.',
            },
            {
                title: 'Reel Editor: export no longer times out',
                description:
                    'Video export requests were previously processed synchronously on the request thread, causing the server to return a 504 Gateway Timeout for long-running FFmpeg jobs. Export now returns immediately with a job ID and processes asynchronously in the background. The editor polls for completion as before.',
            },
            {
                title: 'Subtitle Editor: overlap warning on language selection',
                description:
                    'When switching to a language in the Subtitle Editor, the app now checks for overlapping subtitles (where a cue starts before the previous cue ends). If any are found, a warning dialog is shown listing the exact number of overlapping subtitles, prompting the editor to review and fix them.',
            },
        ],
    },
    {
        id: '2026-04-06-platform-updates',
        title: 'Platform Updates - April 6, 2026',
        publishedOn: 'April 6, 2026',
        summary:
            'Expanded text file translation and download capabilities with multi-file selection, improved upload flows, correct deletion handling for text files, a fix for subtitle time editing, and various UI polish.',
        features: [
            {
                title: 'UI polish: consolidated New button and menu styling',
                description:
                    'The New Folder, New Video, New Dubbing Audio, and New Text buttons have been consolidated into a single Google Drive-style "+ New" dropdown. The Advanced menu section headers now use colour-coded left borders consistent with the rest of the app.',
            },
            {
                title: 'Multi-file translate and download from the Advanced menu',
                description:
                    'The Advanced menu now includes "Translate Text File" and "Download by Language" options under a dedicated Text Only section. Both dialogs support selecting multiple files using checkboxes with a Select All toggle. The translate dialog queues translations for all selected files at once; the download dialog zips only the chosen files for the selected language.',
            },
            {
                title: 'Start AI translation on upload',
                description:
                    'When creating text files (single or bulk), a checkbox lets users opt in to start AI translation immediately after upload. When unchecked, target language slots are created with no content, and the text editor shows a "No translation available" indicator on the right panel instead of a blank editor.',
            },
            {
                title: 'Upload a translated text file',
                description:
                    'Users can now upload a translated HTML or plain-text file directly to a target language slot via the text editor, replacing or seeding translation content without waiting for AI.',
            },
            {
                title: 'Correct deletion for text files',
                description:
                    'Soft-delete, hard-delete, and folder purge now all correctly handle text files, including legacy nodes. Associated Azure Blob storage files are cleaned up on permanent deletion, and deleted files no longer appear as name conflicts when re-creating a file with the same name.',
            },
            {
                title: 'Bug Fix: Subtitle start time can now exceed stop time during editing',
                description:
                    'Previously, setting a start time higher than the stop time in the Subtitle Editor would silently discard the change, making the edit appear to fail. The field now always accepts the entered value. The row is highlighted in red as a visual warning when start ≥ end, allowing users to correct either field freely without losing their input.',
            },
        ],
    },
    {
        id: '2026-04-04-platform-updates',
        title: 'Platform Updates - April 4, 2026',
        publishedOn: 'April 4, 2026',
        summary:
            'This release adds glossary support for text files, improves the text editor layout and usability, and fixes translation status chip accuracy for text files.',
        features: [
            {
                title: 'Text editor layout and formatting improvements',
                description:
                    'The formatting toolbar (Bold, Italic, Underline, Bullet, Numbered, Link, Clear) now uses standard icons and is positioned above the right-hand panel only, so both the source and translation panels are vertically aligned. The page is now full-width. The source panel displays only the source language and is read-only.',
            },
            {
                title: 'Right panel no longer shows source language tab',
                description:
                    'The editable (right) panel now only shows translation languages, removing the source language tab to prevent accidental edits and reduce confusion.',
            },
            {
                title: 'Term Glossary button with tooltip',
                description:
                    'The Glossary button has been renamed to "Term Glossary", moved to the right side of the toolbar, and now shows a tooltip explaining its purpose and that the translation must be at Level 2 or above to add new terms.',
            },
            {
                title: 'Translate dialog shows source language as read-only',
                description:
                    'The Create Translation dialog no longer shows all languages in the source dropdown. It now displays the file\'s source language as a read-only label, preventing incorrect source selection.',
            },
            {
                title: 'Auto-save tooltip always visible on Save button',
                description:
                    'The Save button tooltip now shows the last saved time at all times — including when the button is disabled — so users can confirm when their last auto-save or manual save occurred.',
            },
            {
                title: 'Fixed text-file translation status chip labels',
                description:
                    'Text-file language chips now correctly reflect the actual review level. WIP stages (LEVEL1_WIP, LEVEL2_WIP, LEVEL3_WIP) now show L1, L2, L3 respectively, and a completed translation no longer incorrectly resets to AI when the file has progressed to a human review level.',
            },
            {
                title: 'Drag-and-drop fixed for bulk and single file upload',
                description:
                    'Dragging and dropping files onto the upload zones in the Create Text File dialog now works correctly. Both the single-file and bulk upload zones accept dropped files, highlight on hover, and filter for valid file types (.txt, .text, .html, .htm).',
            },
            {
                title: 'Glossary support for text files',
                description:
                    'Users can now open Glossary Editor directly for text files, review generated and existing terms in one place, save changes safely, and manage existing entries with clear edit/delete actions while preserving glossary integrity.',
            },
            {
                title: 'Rich text editing for text files',
                description:
                    'The text editor now supports rich formatting while typing (without requiring users to write HTML syntax manually). Saved content remains HTML-compatible for downstream translation flow.',
            },
            {
                title: 'HTML file upload support',
                description:
                    'Text file upload now supports .html and .htm files alongside .txt and .text in both single and bulk upload flows.',
            },
            {
                title: 'Text File Details stage selection guard',
                description:
                    'In Text File Details, _WIP process stages are now non-selectable to prevent accidental stage rollback during manual updates.',
            },
            {
                title: 'Admin bulk delete for files',
                description:
                    'Admins can now bulk-select video, text, and audio files directly from folder view and move them to trash together. The selection flow now also includes a visible cancel action in the selection bar for easier exit.',
            },
        ],
    },
    {
        id: '2026-04-03-platform-updates',
        title: 'Platform Updates - April 3, 2026',
        publishedOn: 'April 3, 2026',
        summary:
            'This release introduced the Glossary Editor experience, added glossary edit-conflict protection, and expanded text-file history and level tracking.',
        features: [
            {
                title: 'New Glossary Editor page',
                description:
                    'The Glossary Editor opens as a full page with a two-column layout. The left panel lists all system glossary files with their creation and update history. The right panel lets you select a language pair, generate new terms, review them, and save to the system glossary.',
            },
            {
                title: 'Conflict-safe glossary saving',
                description:
                    'Glossary saves now detect overlapping edits from multiple sessions and block stale saves with a clear conflict message, preventing silent overwrites.',
            },
            {
                title: 'Text file metadata, levels, and restore history',
                description:
                    'From File Details, users can now see text file metadata, track per-language levels (L1/L2/L3/Published), and restore previous language versions. Stage-transition history is retained, while regular save history uses rolling retention windows.',
            },
        ],
    },
    {
        id: '2026-04-02-platform-updates',
        title: 'Platform Updates - April 2, 2026',
        publishedOn: 'April 2, 2026',
        summary:
            'This release improved progress-stage management labels, text-file creation/editor usability, and added seek diagnostics with bug-report integration.',
        features: [
            {
                title: 'Manage Progress Stages naming update',
                description:
                    'The previous "See Progress Stages" action was renamed to "Manage Progress Stages" in menus and dialogs to better reflect edit capability.',
            },
            {
                title: 'Text-file creation and editor workflow improvements',
                description:
                    'Text file creation now validates required fields more clearly, source/target language selection behavior is improved, and editor panes auto-select source/target languages for faster start.',
            },
            {
                title: 'Seek diagnostics and bug-report logging',
                description:
                    'A new diagnostics mode captures seek lifecycle and optional network/request context, and bug reports can attach these logs automatically when enabled.',
            },
        ],
        bugFixes: [
            {
                title: 'Text file language payload fixes',
                description:
                    'Fixed issues where target languages were sent incorrectly and where source language could remain in target selections.',
            },
            {
                title: 'Operational guidance for intermittent seek/reset investigation',
                description:
                    'Recommended flow: enable seek diagnostics, reproduce, submit bug report with browser logs enabled, and diagnostics are included in the payload.',
            },
        ],
        benefits: [
            'Faster triage for intermittent seek/player reset issues that are hard to reproduce locally.',
            'Better root-cause clues from user/session/video/network-tagged diagnostics.',
        ],
    },
    {
        id: '2026-03-27-bug-fixes',
        title: 'Bug Fixes - March 27, 2026',
        publishedOn: 'March 27, 2026',
        summary:
            'This release fixes subtitle editor video seek and loading performance.',
        bugFixes: [
            {
                title: 'Bug Fix: Subtitle editor video seek and slow loading',
                description:
                    'Fixed an issue where seeking in the subtitle editor video player would fail or jump to incorrect positions. Also fixed slow initial loading caused by the video preload setting being changed by an auto-formatter. The player now loads video content eagerly and seeks reliably to any position in the timeline.',
            },
        ],
    },
    {
        id: '2026-03-26-redo-compression',
        title: 'Redo Compression - March 26, 2026',
        publishedOn: 'March 26, 2026',
        summary:
            'Admins can now re-compress any existing video to improve browser loading speed and seeking performance.',
        features: [
            {
                title: 'Redo Compression menu item for videos',
                description:
                    'A new "Redo Compression" option is available in the video context menu (admin only). It re-encodes the video with optimised streaming flags to improve initial load time and timeline seeking. Subtitles and translations are unaffected. Progress is tracked in the processing panel and the video status returns to Completed once done.',
            },
        ],
    },
    {
        id: '2026-03-25-bug-fixes',
        title: 'Bug Fixes - March 25, 2026',
        publishedOn: 'March 25, 2026',
        summary:
            'This release includes critical bug fixes for subtitle seek behavior, user language access permissions, and voice-over language display in admin management.',
        bugFixes: [
            {
                title: 'Bug Fix: Subtitle click seek occasionally jumps to beginning',
                description:
                    'Fixed an intermittent issue where clicking on subtitle text boxes would cause the video to jump to the beginning. This was caused by stale cached subtitle timing data combined with malformed time parsing. Added defensive parsing validation and seek guards to ensure invalid seek commands are rejected.',
            },
            {
                title: 'Bug Fix: English-VO write access not granted automatically',
                description:
                    'Fixed an issue where users with English write access were not automatically receiving English-VO (voice-over) write access. The system now correctly propagates English access permissions to English-VO when updating user language access.',
            },
            {
                title: 'Bug Fix: English-VO not visible in User Management',
                description:
                    'Added English-VO display in the User Management admin interface. When editing a user with English access, the corresponding English-VO access level is now visible and stays synchronized with the English language access setting.',
            },
        ],
    },
    {
        id: '2026-03-24-bundled-feature-release',
        title: 'Bundled Feature Release - March 24, 2026',
        publishedOn: 'March 24, 2026',
        summary:
            'This release improves language-based progress visibility, dubbing controls, cleanup automation, large video loading performance, export stability, live access updates, and bulk download controls.',
        features: [
            {
                title: 'Leaderboard now supports My Languages filtering',
                description:
                    'You can now filter the leaderboard by your selected language, making it easier to compare progress in the language you actively edit.',
            },
            {
                title: 'Improved dubbing voice mixing controls',
                description:
                    'The dubbing flow now offers clearer voice mixing controls, giving you better balance between synthesized voice and original audio.',
            },
            {
                title: 'Automatic cleanup for dubbing audio files (24 hours)',
                description:
                    'Temporary dubbing audio files now auto-clear after 24 hours by default, helping keep storage clean with less manual effort.',
            },
            {
                title: 'Faster loading for large videos',
                description:
                    'Large video editing sessions now open more smoothly with startup optimizations focused on responsiveness and reduced wait time.',
            },
            {
                title: 'Reel export subtitle alignment fixes',
                description:
                    'Exported reel subtitles now better match preview positioning and layout for more consistent final output.',
            },
            {
                title: 'Language access updates apply without re-login',
                description:
                    'Users no longer need to logout and login again to see updated language access levels; changes now refresh automatically in an active session.',
            },
            {
                title: 'Bulk download now supports selective file types and languages',
                description:
                    'You can now choose Original Video, Compressed Video, and SRT files, select multiple subtitle languages, and review availability and estimated file counts before downloading.',
            },
        ],
        // benefits: [
        //     'Better focus and fairness in language-specific leaderboard views.',
        //     'Better control over dubbing output quality.',
        //     'Less manual cleanup effort for temporary audio files.',
        //     'Faster startup for large-file subtitle workflows.',
        // ],
    },
];
